import { useState, useEffect } from 'react'
import OddsCalculator from '../redesign/components/OddsCalculator'
import ResultsSection from '../redesign/components/ResultsSection'
import classesK from '../kalkulatori.module.css'
import InfoBar from '../redesign/components/InfoBar'
import { betkoPopupWithAction } from '../../utils/BetkoPopupModal'
import { areAllFieldsFilled } from '../../utils/calculationDataValidation'
import runCalculator from '../api/runCalculator'

// Shared goal-based 3-way engine: map calculatorType -> English API slug.
const SLUG_BY_TYPE = {
  Hokej: 'ice-hockey',
  Futsal: 'futsal',
  Rukomet: 'handball',
  Vaterpolo: 'water-polo',
}

const Hokej = ({ calculatorType = 'Hokej' }) => {
  const [showInputArea, setShowInputArea] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [data, setData] = useState({
    values: {
      odds: ['', '', ''],
      teams: ['', '', '', '', '', ''],
    },
    results: {
      winningChances: ['', ''],
      expectedGoals: {
        min: 0,
        avg: 0,
        max: 0,
      },
      exactScore: '0 : 0',
      scoreDifference: '',
      suggestedOver: '',
      teamStrength: {
        home: {
          attack: '',
          defense: '',
        },
        away: {
          attack: '',
          defense: '',
        },
        combined: {
          attack: '',
          defense: '',
        },
        matchup: {
          homeAttack_vs_awayDefense: '',
          homeDefense_vs_awayAttack: '',
        },
      },
    },
  })
  const allInputsFilled = areAllFieldsFilled(data.values)

  useEffect(() => {
    if (allInputsFilled) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [allInputsFilled])

  useEffect(() => {
    if (error) {
      betkoPopupWithAction(
        'Greška',
        'Molimo proverite da li su svi podaci ispravno uneti i ponovo pokušajte.'
      )
    }
  }, [error])

  const clearFields = () => {
    setData({
      values: {
        odds: ['', '', ''],
        teams: ['', '', '', '', '', ''],
      },
      results: {
        winningChances: ['', ''],
        expectedGoals: {
          min: 0,
          avg: 0,
          max: 0,
        },
        exactScore: '0 : 0',
        scoreDifference: '',
        suggestedOver: ['', ''],
        teamStrength: {
          home: {
            attack: '',
            defense: '',
          },
          away: {
            attack: '',
            defense: '',
          },
          combined: {
            attack: '',
            defense: '',
          },
          matchup: {
            homeAttack_vs_awayDefense: '',
            homeDefense_vs_awayAttack: '',
          },
        },
      },
    })
    setShowInputArea(false)
  }

  const handleButtonClick = async () => {
    if (showInputArea) {
      clearFields()
      return
    }

    const ok = await handleCalculate()
    if (ok) {
      setShowInputArea((prev) => !prev)
    }
  }

  const buildBody = () => {
    const [
      domacinDao,
      domacinPrimio,
      brojUtakmicaDomacin,
      gostDao,
      gostPrimio,
      brojUtakmicaGost,
    ] = data.values.teams.map((v) => parseFloat(v))

    const [kvota1, kvotaX, kvota2] = data.values.odds.map((v) => parseFloat(v))

    return {
      oddsHome: kvota1,
      oddsDraw: kvotaX,
      oddsAway: kvota2,
      homeGoalsScored: domacinDao,
      homeGoalsConceded: domacinPrimio,
      homeMatchesPlayed: brojUtakmicaDomacin,
      awayGoalsScored: gostDao,
      awayGoalsConceded: gostPrimio,
      awayMatchesPlayed: brojUtakmicaGost,
    }
  }

  const handleCalculate = async () => {
    const slug = SLUG_BY_TYPE[calculatorType] || 'ice-hockey'
    setLoading(true)
    setError(false)
    try {
      const res = await runCalculator('predictions', slug, buildBody())

      setData((prev) => ({
        ...prev,
        results: {
          winningChances: [
            res.homeWinPct.toFixed(2),
            res.drawPct.toFixed(2),
            res.awayWinPct.toFixed(2),
          ],
          expectedGoals: {
            min: res.goalsMin.toFixed(2),
            avg: res.goalsAvg.toFixed(2),
            max: res.goalsMax.toFixed(2),
          },
          exactScore: `${res.correctScoreHome} : ${res.correctScoreAway}`,
          scoreDifference: `${res.winningMargin.toFixed(2)} ( ${res.winningMarginType} )`,
          suggestedOver: [res.suggestion1.toFixed(2), res.suggestion2.toFixed(2)],
          teamStrength: {
            home: {
              attack: res.homeGoalsScoredAvg.toFixed(2),
              defense: res.homeGoalsConcededAvg.toFixed(2),
            },
            away: {
              attack: res.awayGoalsConcededAvg.toFixed(2),
              defense: res.awayGoalsScoredAvg.toFixed(2),
            },
            combined: {
              attack: res.homeScoredAwayConcededAvg.toFixed(2),
              defense: res.homeConcededAwayScoredAvg.toFixed(2),
            },
            matchup: {
              homeAttack_vs_awayDefense: res.homeAwayScoredAvg.toFixed(2),
              homeDefense_vs_awayAttack: res.homeAwayConcededAvg.toFixed(2),
            },
          },
        },
      }))
      return true
    } catch (err) {
      setError(true)
      return false
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={classesK.container}>
      <InfoBar onRefresh={clearFields} calculatorType={calculatorType} />
      <div className={classesK.contentContainer}>
        <div className={classesK.submitWrapper}>
          <span>Unesi nove obračunske podatke</span>
          <button onClick={handleButtonClick} disabled={disabled || loading}>
            {loading
              ? 'Obračunavanje...'
              : showInputArea
              ? 'Unesi obračunske podatke'
              : 'Prikaži rezultate'}
          </button>
        </div>
        {error && !showInputArea && (
          <div className={classesK.sectionTitle}>
            Greška pri obračunu, pokušajte ponovo.
          </div>
        )}
        {!showInputArea && (
          <>
            <div className={classesK.sectionTitle}>
              Upiši kvote za ishod utakmice
            </div>
            <OddsCalculator
              data={data.values}
              setData={setData}
              field={'odds'}
              labelPrefix={['KI 1', 'KI X', 'KI 2']}
              type={['number', 'number', 'number']}
              min={['0.1', '0.1', '0.1']}
              step={['0.1', '0.1', '0.1']}
              rows={3}
              setError={setError}
            />
            <div className={classesK.sectionTitle}>
              Upiši br. golova / br. ut. za oba tima
            </div>
            <OddsCalculator
              field={'teams'}
              data={data.values}
              setData={setData}
              labelPrefix={[
                'DOM +  ( dom. dao golova)',
                'DOM -  ( dom. primio golova)',
                'DOM BR. ( dom. odigrao utakmica)',
                'GOST +  ( gost. dao golova)',
                'GOST -  ( gost. primio golova)',
                'GOST BR. ( gost. odigrao utakmica)',
              ]}
              type={[
                'number',
                'number',
                'number',
                'number',
                'number',
                'number',
              ]}
              step={['1', '1', '1', '1', '1', '1']}
              min={['0', '0', '1', '0', '0', '1']}
              rows={3}
              setError={setError}
            />
          </>
        )}
        {showInputArea && (
          <div>
            <div>
              <ResultsSection
                title={'Ishod utakmice'}
                subtitle={'Konačan ishod'}
                results={data.results.winningChances.map(
                  (chance) => `${chance}%`
                )}
                names={['KI 1', 'KI X', 'KI 2']}
                withPercent
              />
              <ResultsSection
                title={'Golovi'}
                subtitle={'Granice golova'}
                names={[
                  'Očekivani minimalni br. golova na utakmici',
                  'Očekivani srednji br. golova na utakmici',
                  'Očekivani maksimalni br. golova na utakmici',
                ]}
                results={[
                  `${data.results.expectedGoals.min}`,
                  `${data.results.expectedGoals.avg}`,
                  `${data.results.expectedGoals.max}`,
                ]}
                rows={2}
                Í
                breakLastItem={true}
              />
              <ResultsSection
                title={'Tačan rezultat'}
                subtitle={'Opcioni tačan rezultat na utakmici'}
                names={['Predikcija tačnog rezultata utakmice']}
                rows={1}
                results={[data.results.exactScore]}
              />
              <ResultsSection
                title={'Pobeda sa razlikom'}
                subtitle={'Očekivana pobeda favorita sa razlikom'}
                names={['Razlika']}
                rows={1}
                results={[data.results.scoreDifference]}
                allowNegative={true}
              />
              <ResultsSection
                title={'Betko predlog'}
                subtitle={'Predlog igre na broj golova'}
                names={[
                  'Predlog prve granice ( + )',
                  'Predlog druge granice ( + )',
                ]}
                rows={1}
                results={[
                  data.results.suggestedOver[0],
                  data.results.suggestedOver[1],
                ]}
              />
            </div>

            <div>
              <ResultsSection
                title={'Snage timova'}
                subtitle={`Snaga domaćina`}
                results={[
                  data.results.teamStrength.home.attack,
                  data.results.teamStrength.home.defense,
                ]}
                withPercent={false}
                names={['SND ( snaga napada )', 'SOD ( snaga odbrane )']}
              />
              <ResultsSection
                subtitle={`Snaga gosta`}
                results={[
                  data.results.teamStrength.away.attack,
                  data.results.teamStrength.away.defense,
                ]}
                withPercent={false}
                names={['SNG ( snaga napada )', 'SOG ( snaga odbrane )']}
              />
              <ResultsSection
                subtitle={`Snage oba tima zajedno`}
                results={[
                  data.results.teamStrength.combined.attack,
                  data.results.teamStrength.combined.defense,
                ]}
                withPercent={false}
                names={[
                  'SNDG ( snaga napada domaćina i gosta )',
                  'SODG ( snaga odbrane domaćina i gosta )',
                ]}
              />
              <ResultsSection
                subtitle={`Tim proseci odbrana i napada`}
                results={[
                  data.results.teamStrength.matchup.homeAttack_vs_awayDefense,
                  data.results.teamStrength.matchup.homeDefense_vs_awayAttack,
                ]}
                withPercent={false}
                names={[
                  'SND + SOG ( snaga napada domaćin + snaga odbrane gost )',
                  'SOD + SNG ( snaga napada domaćin + snaga odbrane gost )',
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
export default Hokej
